import { formatEnvQueryGroup } from '../../../../src/modules/analysis/env-query-builder/env-query-format';
import {
  ALARM_INTERVAL_UNITS,
  DEFAULT_ALARM_INTERVAL_UNIT,
  alarmIntervalToSeconds,
} from '../../../../src/modules/analysis/alarm-rule/alarm-interval';
import { getAlarmRuleErrorMessage, selectionToDataWindowSeconds } from '../../../../src/modules/analysis/filterParameterMapping';
import { getAlarmRuleCriteriaErrors } from '../../../../src/modules/analysis/alarm-rule/alarm-rule-validation';

function AnalysisController(
  analysisService,
  eventsExportService,
  notificationService,
  CommonService,
  userViewService,
  userService,
  toasterService,
  eventService,
  chartColorService,
  filterPresetService,
  fleetService,
  $translate,
  $interval,
  $scope,
  $uibModal,
  __env
) {
  const ctrl = this;
  const tryCatch = CommonService.tryCatch;
  const consistEventPageSize = 500;
  const eventHistoryPageSize = 100;
  let eventHistoryGeneration = 0;
  let pendingEventHistoryReload = null;
  let lastEventHistoryRequest = null;
  let destroyed = false;
  const hasPositiveNumber = value => Number(value) > 0;
  const createEmptyEnvFilters = () => ({ query: { combinator: 'and', rules: [] }, filterJson: '' });

  ctrl.$onInit = () => {
    userViewService.setView('analysis');

    // Default values
    ctrl.initialized = false;
    ctrl.carList = [{ title: 'ALL', value: 'ALL' }];
    ctrl.eventInfo = [];
    ctrl.eventInfoFiltered = [];
    ctrl.totalEvents = 0;
    ctrl.oldHeatMapEndDate = null;
    ctrl.oldHeatMapStartDate = null;
    ctrl.chartLoading = false;
    ctrl.selectedSubs = [];
    ctrl.prevSelectors = [];
    ctrl.analysisConnection = true;
    ctrl.chartPresentation = true;
    ctrl.eventHistory = [];
    ctrl.eventsLoading = false;
    ctrl.loadBusy = false;
    ctrl.eventHistoryHasMore = false;
    ctrl.eventHistoryError = null;
    ctrl.sortType = null;
    ctrl.reversed = true;
    ctrl.previousLoadWasHeatmap = false;
    ctrl.previousLoadWasEventHistory = false;
    ctrl.envColumnOptions = [];
    // LU exposes a large environment schema, so default to a useful subset.
    ctrl.defaultEnvColumns = ['force event cleared', 'car type', 'unit number', 'force event occured'];
    ctrl.hasAccessAlarms = userService.hasAccessAlarms();
    ctrl.supportsAdvancedAnalysisFilters = __env.profile === 'LU';
    ctrl.showPresets = __env.profile === 'LU';
    // Preserve existing LU Event History controls independently of alarm access.
    ctrl.supportsEventHistoryFilters = __env.profile === 'LU' || ctrl.hasAccessAlarms;
    ctrl.hasCuratedEnvColumns = __env.profile === 'LU';
    ctrl.usesAlternateEventDisplay = __env.profile === 'MELCO';
    ctrl.usesQualifierEventColor = __env.profile === 'VALE';
    ctrl.showsActiveCarLabel = __env.profile === 'QR';
    ctrl.operator = __env.operator;
    // Flip to true to re-enable the Data Scope (Unit/Consist) control; all underlying logic is kept intact.
    ctrl.dataScopeFeatureEnabled = false;
    ctrl.dataScope = 'unit';
    // Tab order is Alarms (0) | Rules (1) | Event History (2); default to Event History so existing users see unchanged behavior.
    ctrl.activeAnalysisTab = 2;
    ctrl.prevActiveAnalysisTab = ctrl.activeAnalysisTab;
    // this backs the Consist-scope grouped table and uses {trainId, cars} entries from fleetService.
    ctrl.dataScopeConsistList = [];
    // Narrowed by the Consist filter dropdown; this is what the grouped table actually renders.
    ctrl.dataScopeConsistListFiltered = [];
    ctrl.consistEvents = {};
    ctrl.consistEventsLoading = {};
    ctrl.consistSortType = 'ACTIVATED';
    // Mirrors the Unit table's per-column reversed flags (see event-history-table.controller.js).
    ctrl.consistReversed = { CONSIST: true, CAR: true, EVENT_ID: true, ACTIVATED: true, TITLE: true, ACTION: true };

    // Filter presets - managed by FilterPresets.js--React component
    ctrl.filterPresets = [];
    ctrl.selectedPreset = null;
    ctrl.presetsLoading = false;
    ctrl.presetsError = null;
    ctrl.envQueryValid = false;
    ctrl.dataWindowUnits = [
      { value: 'date', label: 'Date' },
      { value: 'day', label: 'Day' },
      { value: 'hours', label: 'Hours' },
      { value: 'minutes', label: 'Minutes' },
      { value: 'seconds', label: 'Seconds' },
    ];
    ctrl.dataWindowMode = 'date';
    ctrl.dataWindowValue = null;
    // Matches the legacy TimePicker default (72h / 3 days) used by getDefaultDataWindowRange().
    ctrl.dataWindowDefaultHours = 72;// hours
    ctrl.dataWindowDateRange = {
      startDate: null,
      endDate: null,
    };

    ctrl.selection = {
      startDate: null,
      endDate: null,
      events: [],
      envFilters: createEmptyEnvFilters(),
      envData: [],
      subsystems: [],
      consist: null,
      cars: [],
      chartTypeConfig: ctrl.chartConfigList[5],
      active: null,
    };
    // Fields will be populated from metadata endpoint - single source of truth
    ctrl.envDataFields = [];
    ctrl.envDataFieldValues = {}; // Distinct values for each field {fieldName: [value1, value2, ...]}
    ctrl.envDataFieldMetadata = []; // Metadata from backend with types and display names
    ctrl.searchTerm = "";
    ctrl.createRuleOccurrences = null;
    ctrl.createRuleIntervalUnits = ALARM_INTERVAL_UNITS;
    ctrl.createRuleIntervalValue = null;
    ctrl.createRuleIntervalUnit = DEFAULT_ALARM_INTERVAL_UNIT;
    setChartConfigDefaults();
    initData();
  };

  const isEventHistoryTabActive = () =>
    !ctrl.hasAccessAlarms ||
    ctrl.selection.chartTypeConfig.value !== 'eventHistoryData' ||
    ctrl.activeAnalysisTab === 2;

  ctrl.$doCheck = () => {
    if (ctrl.activeAnalysisTab !== ctrl.prevActiveAnalysisTab) {
      if (ctrl.activeAnalysisTab === 2 && ctrl.selection.chartTypeConfig.value === 'eventHistoryData') {
        loadGraphData();
      }
      ctrl.prevActiveAnalysisTab = ctrl.activeAnalysisTab;
    }

    // Reset selectors the new chart type doesn't use before diffing, so the reset is part of the same load
    // instead of a follow-up change (e.g. LU's default envData columns) that triggers a duplicate request.
    if (!angular.equals(ctrl.selection.chartTypeConfig.selectors, ctrl.prevSelectors)) {
      const selectors = ['chartType', 'consist', 'cars', 'subsystems', 'events', 'envData'];
      const notIncluded = _.difference(selectors, ctrl.selection.chartTypeConfig.selectors);
      _.each(notIncluded, value => {
        ctrl.resetSelection(value);
      });
      ctrl.prevSelectors = angular.copy(ctrl.selection.chartTypeConfig.selectors);
    }

    if (!ctrl.skipAutoLoad && !angular.equals(ctrl.selection, ctrl.prevSelection)) {
      if (!ctrl.oldHeatMapEndDate && !ctrl.oldHeatMapStartDate && ctrl.initialized) {
        loadGraphData();
      }
      ctrl.prevSelection = angular.copy(ctrl.selection);
    }
  };

  ctrl.$onDestroy = () => {
    destroyed = true;
    eventHistoryGeneration += 1;
    pendingEventHistoryReload = null;
    $interval.cancel(ctrl.timeoutPromise);
    ctrl.analysisConnection = true;
  };

  ctrl.updateDates = (startDate, endDate) => {
    ctrl.selection.startDate = startDate;
    ctrl.selection.endDate = endDate;
    ctrl.dataWindowDateRange.startDate = startDate;
    ctrl.dataWindowDateRange.endDate = endDate;
  };

  const dataWindowUnitToMilliseconds = {
    seconds: 1000,
    minutes: 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    hours: 60 * 60 * 1000,
  };

  // Default data window is 72 hours (3 days), matching TimePicker.js's own default and ctrl.dataWindowDefaultHours.
  const getDefaultDataWindowRange = () => {
    const endDate = new Date();
    endDate.setSeconds(0);
    endDate.setMilliseconds(0);
    const startDate = new Date(endDate.getTime() - ctrl.dataWindowDefaultHours * dataWindowUnitToMilliseconds.hours);
    return { startDate, endDate };
  };

  const computeDataWindowValueFromRange = (startDate, endDate, unit) => {
    const windowSize = dataWindowUnitToMilliseconds[unit];
    if (!startDate || !endDate || !windowSize) {
      const defaultRange = getDefaultDataWindowRange();
      startDate = defaultRange.startDate;
      endDate = defaultRange.endDate;
    }
    return Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / windowSize));
  };

  ctrl.onDataWindowModeChange = () => {
    if (ctrl.dataWindowMode === 'date') {
      if (ctrl.dataWindowDateRange.startDate && ctrl.dataWindowDateRange.endDate) {
        ctrl.selection.startDate = ctrl.dataWindowDateRange.startDate;
        ctrl.selection.endDate = ctrl.dataWindowDateRange.endDate;
      }
      return;
    }

    ctrl.dataWindowValue = computeDataWindowValueFromRange(
      ctrl.dataWindowDateRange.startDate,
      ctrl.dataWindowDateRange.endDate,
      ctrl.dataWindowMode
    );
    ctrl.updateRelativeDataWindow();
  };

  ctrl.updateRelativeDataWindow = () => {
    const amount = Number(ctrl.dataWindowValue);
    const windowSize = dataWindowUnitToMilliseconds[ctrl.dataWindowMode];

    if (!Number.isFinite(amount) || amount <= 0 || !windowSize) {
      return;
    }

    const endDate = ctrl.dataWindowDateRange.endDate || ctrl.selection.endDate || new Date();
    const startDate = new Date(endDate.getTime() - amount * windowSize);
    if (!Number.isFinite(startDate.getTime())) return;

    ctrl.selection.startDate = startDate;
    ctrl.selection.endDate = endDate;
  };

  ctrl.resetDataWindow = () => {
    const defaultRange = getDefaultDataWindowRange();
    ctrl.dataWindowDateRange.startDate = defaultRange.startDate;
    ctrl.dataWindowDateRange.endDate = defaultRange.endDate;
    ctrl.selection.startDate = defaultRange.startDate;
    ctrl.selection.endDate = defaultRange.endDate;

    if (ctrl.dataWindowMode !== 'date') {
      ctrl.dataWindowValue = computeDataWindowValueFromRange(
        defaultRange.startDate,
        defaultRange.endDate,
        ctrl.dataWindowMode
      );
    }
  };

  // DateFilter (React) callbacks - it passes new values directly instead of two-way ng-model binding.
  ctrl.onDateFilterStartDateChange = startDate => {
    ctrl.dataWindowDateRange.startDate = startDate;
    ctrl.selection.startDate = startDate;
  };

  ctrl.onDateFilterEndDateChange = endDate => {
    ctrl.dataWindowDateRange.endDate = endDate;
    ctrl.selection.endDate = endDate;
  };

  ctrl.onDateFilterModeChange = mode => {
    ctrl.dataWindowMode = mode;
    ctrl.onDataWindowModeChange();
  };

  ctrl.onDateFilterValueChange = value => {
    ctrl.dataWindowValue = value;
    ctrl.updateRelativeDataWindow();
  };

  // Sets both dates atomically to avoid a stale-closure overwrite from two separate calls.
  ctrl.onDateFilterRangeInit = (startDate, endDate) => {
    ctrl.dataWindowDateRange.startDate = startDate;
    ctrl.dataWindowDateRange.endDate = endDate;
    ctrl.selection.startDate = startDate;
    ctrl.selection.endDate = endDate;
  };

  // CreateAlarmRule (React) callbacks - it passes new values directly instead of two-way ng-model binding.
  ctrl.onCreateRuleOccurrencesChange = occurrences => {
    ctrl.createRuleOccurrences = occurrences;
  };

  ctrl.onCreateRuleIntervalValueChange = intervalValue => {
    ctrl.createRuleIntervalValue = intervalValue;
  };

  ctrl.onCreateRuleIntervalUnitChange = intervalUnit => {
    ctrl.createRuleIntervalUnit = intervalUnit;
  };

  ctrl.hasExactlyOneEventSelected = () => ctrl.selection.events.length >= 1;

  ctrl.getCreateRuleIntervalSeconds = () =>
    alarmIntervalToSeconds(ctrl.createRuleIntervalValue, ctrl.createRuleIntervalUnit);

  ctrl.getCreateRuleDataWindowSeconds = () => selectionToDataWindowSeconds({
    ...ctrl.selection,
    dataWindowMode: ctrl.dataWindowMode,
    dataWindowValue: ctrl.dataWindowValue,
  }, ctrl.dataWindowDefaultHours);

  ctrl.isCreateRuleIntervalLongerThanDataWindow = () =>
    ctrl.getCreateRuleIntervalSeconds() > ctrl.getCreateRuleDataWindowSeconds();

  const getAlarmSettingsDisabledReason = action => {
    if (!ctrl.hasExactlyOneEventSelected()) return `Select one event to ${action}`;
    const errors = getAlarmRuleCriteriaErrors({
      alarmThresholdCount: ctrl.createRuleOccurrences,
      alarmIntervalValue: ctrl.createRuleIntervalValue,
      alarmIntervalUnit: ctrl.createRuleIntervalUnit,
    });
    if (errors.alarmThresholdCount) return `Threshold Count: ${errors.alarmThresholdCount}`;
    if (errors.alarmIntervalValue) return `Within: ${errors.alarmIntervalValue}`;
    if (ctrl.isCreateRuleIntervalLongerThanDataWindow()) return 'The interval cannot be longer than the data window';
    return '';
  };

  ctrl.getCreateRuleDisabledReason = () => getAlarmSettingsDisabledReason('create a rule');

  ctrl.getRunReportDisabledReason = () => {
    const hasDataWindow = ctrl.dataWindowMode === 'date'
      ? Boolean(ctrl.selection.startDate && ctrl.selection.endDate)
      : hasPositiveNumber(ctrl.dataWindowValue) && Boolean(dataWindowUnitToMilliseconds[ctrl.dataWindowMode]);
    if (!hasDataWindow) return 'Select a data window';
    return getAlarmSettingsDisabledReason('run a report');
  };

  ctrl.isCreateRuleDisabled = () => Boolean(ctrl.getCreateRuleDisabledReason());

  ctrl.isRunReportDisabled = () => Boolean(ctrl.getRunReportDisabledReason());

  // Consist rows come from the fleet list so every consist shows before its events are paged in.
  const loadDataScopeConsistList = async () => {
    try {
      ctrl.dataScopeConsistList = (await fleetService.getTrainsWithCars()) || [];
    } catch (error) {
      console.error('Failed to load consist list:', error);
      ctrl.dataScopeConsistList = [];
    } finally {
      updateDataScopeConsistListFiltered();
      $scope.$applyAsync();
    }
  };
  
  // Keeps the grouped table's parent rows in sync with the Consist filter dropdown.
  const updateDataScopeConsistListFiltered = () => {
    ctrl.dataScopeConsistListFiltered = ctrl.selection.consist
      ? ctrl.dataScopeConsistList.filter(({ trainId }) => trainId === ctrl.selection.consist.value)
      : ctrl.dataScopeConsistList;
  };
  
  // Expanding a consist fetches its own events so it does not depend on the paged event history.
  // `force` re-fetches even if cached, used when the consist sort order changes.
  ctrl.loadConsistEvents = async (consistId, force = false) => {
    if (ctrl.consistEventsLoading[consistId]) return;
    if (!force && ctrl.consistEvents[consistId]) return;

    const consist = ctrl.dataScopeConsistList.find(({ trainId }) => trainId === consistId);
    if (!consist || !consist.cars?.length) return;

    ctrl.consistEventsLoading = { ...ctrl.consistEventsLoading, [consistId]: true };
    $scope.$applyAsync();

    try {
      const parameters = getEventListParameters({
        reload: true,
        sortType: ctrl.consistSortType,
        reversed: ctrl.consistReversed[ctrl.consistSortType],
      });
      parameters.skip = 0;
      parameters.count = consistEventPageSize;
      parameters.cars = consist.cars;

      const data = await ctrl.fetchEvents(parameters);
      if (data) {
        addNewEnvDataColumns(data);
        ctrl.consistEvents = { ...ctrl.consistEvents, [consistId]: data };
      }
    } catch (error) {
      console.error('Failed to load events for consist:', consistId, error);
    } finally {
      ctrl.consistEventsLoading = { ...ctrl.consistEventsLoading, [consistId]: false };
      $scope.$applyAsync();
    }
  };

  // Same backend sort types as the Unit table; re-fetches any consists already expanded.
  ctrl.sortConsistColumn = (sortType, expandedConsistIds = []) => {
    if (ctrl.loadBusy) return;
    ctrl.consistSortType = sortType;
    ctrl.consistReversed[sortType] = !ctrl.consistReversed[sortType];
    ctrl.consistEvents = {};
    expandedConsistIds.forEach(consistId => ctrl.loadConsistEvents(consistId, true));
  };

  ctrl.updateDataScope = dataScope => {
    ctrl.dataScope = dataScope === 'consist' ? 'consist' : 'unit';
    // Cars selector is disabled in Consist scope; $doCheck reloads if this changes the query.
    ctrl.selection.cars = [];
    if (ctrl.dataScope === 'consist' && !ctrl.dataScopeConsistList.length) {
      loadDataScopeConsistList();
    }
  };

  ctrl.subsystemChange = () => {
    createFilteredEventInfo();
  };

  ctrl.consistChange = () => {
    if (ctrl.selection.consist && ctrl.selection.consist.cars) {
      const consistCars = ctrl.selection.consist.cars;
      ctrl.carList = ctrl.carListOriginal.filter(c => consistCars.includes(c.value));
      // Car selector is hidden while a consist is selected; don't let a stale selection override it.
      ctrl.selection.cars = [];
    } else {
      ctrl.carList = angular.copy(ctrl.carListOriginal);
    }
  };

  ctrl.chartConfigList = [
    {
      value: 'groupsById',
      title: $translate.instant('analysis.alarm-analysis-gridlet-module.chart-selections.groups-by-id'),
      chartTypes: ['pie', 'column'],
      selectors: ['chartType', 'consist', 'cars', 'subsystems'],
      dataLoad: 'loadGroupsById',
      clickValue: 'eventsByGroup',
      clickLoad: 'loadEventsByGroup',
      clickInfo: $translate.instant('analysis.alarm-analysis-gridlet-module.chart-selections.groups-by-id-click-info'),
      type: 'chart',
      visible: true,
    },
    {
      value: 'eventsByGroup',
      title: $translate.instant('analysis.alarm-analysis-gridlet-module.chart-selections.events-by-group'),
      chartTypes: ['pie', 'column'],
      selectors: ['chartType', 'consist', 'cars', 'subsystems', 'events'],
      dataLoad: 'loadEventsByGroup',
      clickValue: 'eventsStacked',
      clickLoad: 'loadEventsStacked',
      clickInfo: $translate.instant(
        'analysis.alarm-analysis-gridlet-module.chart-selections.events-by-group-click-info'
      ),
      type: 'chart',
      visible: true,
    },
    {
      value: 'eventsStacked',
      title: $translate.instant('analysis.alarm-analysis-gridlet-module.chart-selections.events-stacked'),
      chartTypes: ['column'],
      selectors: ['chartType', 'consist', 'cars', 'subsystems', 'events'],
      dataLoad: 'loadEventsStacked',
      clickValue: 'groupsById',
      clickLoad: 'loadGroupsById',
      clickInfo: $translate.instant(
        'analysis.alarm-analysis-gridlet-module.chart-selections.events-stacked-click-info'
      ),
      type: 'chart',
      visible: true,
    },
    {
      value: 'groupsStacked',
      title: $translate.instant('analysis.alarm-analysis-gridlet-module.chart-selections.groups-stacked'),
      chartTypes: ['column'],
      selectors: ['chartType', 'consist', 'cars', 'subsystems'],
      dataLoad: 'loadGroupsStacked',
      clickValue: 'groupsById',
      clickLoad: 'loadGroupsById',
      clickInfo: $translate.instant(
        'analysis.alarm-analysis-gridlet-module.chart-selections.groups-stacked-click-info'
      ),
      type: 'chart',
      visible: true,
    },
    {
      value: 'faultHeatMap',
      title: $translate.instant('analysis.alarm-analysis-gridlet-module.chart-selections.event-heat-map'),
      chartTypes: ['heatmap'],
      selectors: ['chartType', 'consist', 'cars', 'subsystems', 'events'],
      dataLoad: 'loadFaultHeatMap',
      clickValue: 'eventsByGroup',
      clickLoad: 'loadEventsByGroup',
      clickInfo: $translate.instant(
        'analysis.alarm-analysis-gridlet-module.chart-selections.event-heat-map-click-info'
      ),
      type: 'chart',
      visible: true,
    },
    {
      value: 'eventHistoryData',
      title: $translate.instant('analysis.alarm-analysis-gridlet-module.chart-selections.event-history-data'),
      chartTypes: ['table'],
      selectors: ['chartType', 'consist', 'cars', 'subsystems', 'events', 'envData'],
      dataLoad: 'loadEventHistoryData',
      clickValue: '',
      clickLoad: '',
      clickInfo: '',
      type: 'table',
      visible: true,
    },
    {
      value: 'consistHistoryData',
      title: $translate.instant('analysis.alarm-analysis-gridlet-module.chart-selections.consist-history-data'),
      chartTypes: ['table'],
      selectors: [],
      dataLoad: 'loadConsistHistoryData',
      clickValue: '',
      clickLoad: '',
      clickInfo: '',
      type: 'table',
      visible: __env.profile === 'LU',
    },
  ].filter(configType => configType.visible);

  const initData = async () => {
    await loadConsistList();
    await loadCarList();
    ctrl.subsystemInfo = await loadSubsystemInfo();
    await loadEventInfo();
    ctrl.initialized = true;
    loadGraphData();
  };

  const loadConsistList = async () => {
    return fleetService.getTrainsWithCars().then(data => {
      if (!angular.equals(ctrl.consistListOriginal, data)) {
        ctrl.consistListOriginal = data;
        ctrl.consistList = [];
        _.each(data.sort((a, b) => (a.trainId > b.trainId) ? 1 : ((b.trainId > a.trainId) ? -1 : 0)), consist => {
          ctrl.consistList.push({ title: consist.trainId, value: consist.trainId, cars: consist.cars });
        });
      }
    });
  };

  const loadCarList = async () => {
    return analysisService.carList().then(data => {
      if (!angular.equals(ctrl.carListOriginalData, data)) {
        ctrl.carListOriginalData = data;
        ctrl.carList = [];
        _.each(data, car => {
          ctrl.carList.push({ title: car, value: car, selected: false });
        });
        ctrl.carListOriginal = angular.copy(ctrl.carList);
      }
    });
  };

  const loadSubsystemInfo = () => {
    return analysisService.subsystemInfo().then(data => {
      if (!angular.equals(ctrl.subsystemInfo, data)) {
        return data;
      }
    });
  };

  const loadEventInfo = () => {
    return analysisService.eventInfo().then(data => {
      if (!angular.equals(ctrl.eventInfo, data)) {
        ctrl.eventInfo = [];
        _.each(data, event => {
          ctrl.eventInfo.push({
            title: event.name,
            value: event.id,
            subsystem: event.subsystemName,
            subsystemId: event.subsystemId,
          });
        });
      }
      ctrl.eventInfoFiltered = angular.copy(ctrl.eventInfo);
    });
  };

  const loadEnvDataMetadata = () => {
    return analysisService.envDataVariables().then(data => {
      // Transform backend response to normalize field names
      ctrl.envDataFieldMetadata = data.map(field => ({
        name: field.element_display_name || field.name, // Use display name for matching event data
        displayName: field.element_display_name || field.displayName,
        variableType: field.element_var_type || field.variableType,
        bitSize: field.element_bit_size || field.bitSize,
        offset: field.element_offset || field.offset,
        translationReference: field.element_translation_reference,
        hasTranslation: !!field.element_translation_reference
      }));
      // Use ALL fields from metadata endpoint as the single source of truth
      ctrl.envDataFields = ctrl.envDataFieldMetadata.map(field => field.name);
      ctrl.envColumnOptions = [...ctrl.envDataFields];
      
      // Pre-populate field values based on type
      ctrl.envDataFieldMetadata.forEach(field => {
        const fieldName = field.name;
        // Boolean fields get true/false options immediately
        if (field.variableType === 'BOOL') {
          ctrl.envDataFieldValues[fieldName] = ['true', 'false'];
        } else {
          // Other fields start empty, will be populated from event data
          ctrl.envDataFieldValues[fieldName] = [];
        }
      });
      
    }).catch(error => {
      console.error('Failed to load environment data metadata:', error);
      // Fallback to default fields if API fails
      ctrl.envDataFieldMetadata = [];
    });
  };

  const ensureChartDateRange = () => {
    if (ctrl.selection.chartTypeConfig.type !== 'chart' || ctrl.selection.chartTypeConfig.value === 'faultHeatMap') return;
    if (ctrl.selection.startDate && ctrl.selection.endDate) return;
    const defaultRange = getDefaultDataWindowRange();
    const endDate = ctrl.selection.endDate || ctrl.dataWindowDateRange.endDate || defaultRange.endDate;
    const startDate = ctrl.selection.startDate || ctrl.dataWindowDateRange.startDate ||
      new Date(endDate.getTime() - ctrl.dataWindowDefaultHours * dataWindowUnitToMilliseconds.hours);
    ctrl.updateDates(startDate, endDate);
  };

  const loadGraphData = event => {
    if (!ctrl.analysisConnection || !ctrl.initialized || !isEventHistoryTabActive()) {
      return;
    }
    ensureChartDateRange();
    if (ctrl.timeoutPromise) $interval.cancel(ctrl.timeoutPromise);
    ctrl.categoryLimitExceeded = false;
    if (ctrl.previousLoadWasHeatmap && ctrl.selection.chartTypeConfig.value !== 'faultHeatMap') {
      setChartConfigDefaults();
      ctrl.previousLoadWasHeatmap = false;
    } else if (ctrl.previousLoadWasEventHistory && ctrl.selection.chartTypeConfig.value !== 'eventHistoryData') {
      setChartConfigDefaults();
      ctrl.previousLoadWasEventHistory = false;
    }
    if (ctrl.selection.chartTypeConfig.type === 'table') {
      ctrl.chartLoading = false;
      ctrl.chartPresentation = false;
      if (ctrl.selection.chartTypeConfig.value === "eventHistoryData") {
        ctrl[ctrl.selection.chartTypeConfig.dataLoad]({ reload: true });
      } else {
        ctrl[ctrl.selection.chartTypeConfig.dataLoad](true);
      }
    } else {
      ctrl.chartLoading = true;
      ctrl.chartPresentation = true;
      if (event && ctrl.selection.chartTypeConfig.clickLoad && ctrl.selection.chartTypeConfig.clickLoad.length > 0) {
        ctrl[ctrl.selection.chartTypeConfig.clickLoad](event);
      } else {
        ctrl[ctrl.selection.chartTypeConfig.dataLoad]();
      }
    }
  };

  const setChartConfigDefaults = () => {
    ctrl.config = {
      chart: {
        type: 'column',
      },
      xAxis: {
        type: 'category',
      },
      yAxis: {
        allowDecimals: false,
      },
      legend: {},
      tooltip: {
        formatter: function () {
          if (ctrl.config.chart.type === 'column') {
            let tip = '<b>' + this.key;
            let stackSelected = this.point.series.options.stack;
            let categorySelected = this.point.category;
            tip += '<br/><br/>';

            $.each(this.series.chart.series, function (i, s) {
              if (s.options.stack === stackSelected) {
                $.each(s.data, function (j, point) {
                  if (point.category === categorySelected && point.y > 0)
                    tip +=
                      '<span style="color:' +
                      point.series.color +
                      '">\u25CF</span> ' +
                      s.name +
                      ': ' +
                      point.y +
                      '<br>';
                });
              }
            });
            return tip;
          } else {
            return '<span style="color:' + this.color + '">\u25CF</span> <b> ' + this.key + ': </b>' + this.y;
          }
        },
        shared: false,
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: function (e) {
                loadGraphData(angular.copy(e.point));
              },
            },
          },
        },
      },
      series: [],
    };
    $scope.$broadcast('reload');
  };

  const createHeatMapConfig = data => {
    ctrl.config = {
      series: [
        {
          name: 'Faults',
          borderWidth: 1,
          data: data.data,
          dataLabels: {
            enabled: true,
            color: '#000000',
          },
        },
      ],
      xAxis: {
        categories: data.xAxis,
      },
      yAxis: {
        categories: data.yAxis,
        title: null,
        allowDecimals: false,
      },
      colorAxis: {
        min: 0,
        minColor: '#FFFFFF',
        maxColor: '#337ab7',
      },
      legend: {
        align: 'right',
        layout: 'vertical',
        margin: 0,
        verticalAlign: 'top',
        y: 25,
        symbolHeight: 280,
      },
      tooltip: {
        formatter: function () {
          let tooltip = '';
          if (this.point) {
            tooltip = (
              '<b>' +
              this.point.value +
              '</b> Faults on <br><b>' +
              this.series.xAxis.categories[this.point.x] +
              '</b> on <br><b>' +
              DateTime.fromISO(this.series.yAxis.categories[this.point.y]).toFormat('dd.MM.yyyy') +
              '</b>'
            );
          } 
          return tooltip;
        },
      },
      chart: {
        type: 'heatmap',
        marginTop: 40,
        marginBottom: 80,
        plotBorderWidth: 1,
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: function (e) {
                loadHeatClick(angular.copy(e.point));
              },
            },
          },
        },
      },
    };
    ctrl.previousLoadWasHeatmap = true;
  };

  const loadHeatClick = event => {
    ctrl.chartLoading = true;
    ctrl.selection.cars.push({ value: ctrl.config.xAxis.categories[event.x] });
    ctrl.selection.startDate = new Date(ctrl.config.yAxis.categories[event.y]);
    ctrl.selection.endDate = new Date(ctrl.config.yAxis.categories[event.y]);
    setChartConfigDefaults();
    ctrl.previousLoadWasHeatmap = false;
    ctrl[ctrl.selection.chartTypeConfig.clickLoad]();
    setSelectionRight();
  };

  /*
  * Event spread by subsystem
  */
  ctrl.loadGroupsById = event => {
    if (!ctrl.analysisConnection || !ctrl.initialized || ctrl.selection?.startDate === null || ctrl.selection?.endDate === null) {
      ctrl.chartLoading = false;
      return;
    }
    let parameters = getParameters();

    if (ctrl.selection.subsystems.length > 0) {
      parameters.subsystemIds = [];
      for (const subsystem of ctrl.selection.subsystems) {
        if (subsystem.value) {
          parameters.subsystemIds.push(subsystem.value);
        } else if (subsystem.id != null) {
          parameters.subsystemIds.push(subsystem.id);
        }
      }
    }
    if (event) {
      parameters.cars = [];
      parameters.cars.push(event.name);
    }
    analysisService.groupsById(parameters).then(data => {
      chartColorService.fillDataWithColors(data);
      if (!angular.equals(ctrl.config.series, data)) {
        ctrl.config.series = data;
        ctrl.setShowData();
        if (event) {
          ctrl.selection.cars.push({ value: event.name });
          setSelectionRight();
        }
        ctrl.totalEvents = data[0].count;
        $scope.$broadcast('reflow');
      }
      ctrl.chartLoading = false;
    });
  };

  /*
  * Event amounts
  */
  ctrl.loadEventsByGroup = event => {
    if (!ctrl.analysisConnection || !ctrl.initialized) {
      return;
    }
    let parameters = getParameters();
    parameters.subsystemIds = [];
    if (event) {
      parameters.subsystemIds.push(event.drillId);
    }
    for (const subsystem of ctrl.selection.subsystems) {
      if (subsystem.value) {
        parameters.subsystemIds.push(subsystem.value);
      } else if (subsystem.id != null) {
        parameters.subsystemIds.push(subsystem.id);
      }
    }

    analysisService.eventsByGroup(parameters).then(data => {
      chartColorService.fillDataWithColors(data);
      if (!angular.equals(ctrl.config.series, data)) {
        ctrl.config.series = data;
        ctrl.categoryLimitExceeded = ctrl.config.series[0].data.length > 200;
        ctrl.setShowData();

        if (event) {
          ctrl.selection.subsystems = [];
          ctrl.selection.subsystems.push({ id: event.drillId });
          setSelectionRight();
        }
        ctrl.totalEvents = data[0].count;
        $scope.$broadcast('reflow');
      }
      ctrl.chartLoading = false;
    });
  };

  /*
  * Events on cars
  */
  ctrl.loadEventsStacked = event => {
    if (!ctrl.analysisConnection || !ctrl.initialized) {
      return;
    }
    let parameters = getParameters();
    if (event) {
      parameters.eventIds = [];
      parameters.eventIds.push(event.drillId);
    }
    parameters.subsystemIds = [];
    for (const subsystem of ctrl.selection.subsystems) {
      if (subsystem.value) {
        parameters.subsystemIds.push(subsystem.value);
      } else if (subsystem.id != null) {
        parameters.subsystemIds.push(subsystem.id);
      }
    }
    analysisService.eventsStacked(parameters).then(data => {
      chartColorService.fillDataWithColors(data);
      if (!angular.equals(ctrl.config.series, data)) {
        ctrl.config.series = data;
        ctrl.categoryLimitExceeded = ctrl.config.series.length > 100;
        ctrl.setShowData();
        if (event) {
          ctrl.selection.events.push({ value: event.drillId });
          setSelectionRight();
        }
        ctrl.totalEvents = data && data.length > 0 && data[0].count;
        $scope.$broadcast('reflow');
      }
      ctrl.chartLoading = false;
    });
  };

  /*
  * Events on cars by subsystem
  */
  ctrl.loadGroupsStacked = () => {
    if (!ctrl.analysisConnection || !ctrl.initialized) {
      return;
    }
    let parameters = getParameters();
    parameters.subsystemIds = [];
    for (const subsystem of ctrl.selection.subsystems) {
      if (subsystem.value) {
        parameters.subsystemIds.push(subsystem.value);
      } else if (subsystem.id != null) {
        parameters.subsystemIds.push(subsystem.id);
      }
    }
    analysisService.groupsStacked(parameters).then(data => {
      chartColorService.fillDataWithColors(data);
      if (!angular.equals(ctrl.config.series, data)) {
        ctrl.config.series = data;
        ctrl.setShowData();
        $scope.$broadcast('reflow');
      }
      ctrl.totalEvents = data && data.length > 0 && data[0].count;
      ctrl.chartLoading = false;
    });
  };

  ctrl.loadFaultHeatMap = () => {
    if (!ctrl.analysisConnection || !ctrl.initialized) {
      return;
    }
    let parameters = getParameters();

    parameters.subsystemIds = [];
    for (const subsystem of ctrl.selection.subsystems) {
      if (subsystem.value) {
        parameters.subsystemIds.push(subsystem.value);
      } else if (subsystem.id != null) {
        parameters.subsystemIds.push(subsystem.id);
      }
    }
    analysisService.eventHeatMap(parameters).then(data => {
      if (data.data) {
        createHeatMapConfig(data);
        ctrl.setShowData();
        const oldStartDateFormatted = DateTime.fromJSDate(ctrl.oldHeatMapStartDate).toFormat('yyyy-MM-dd');
        const oldEndDateFormatted = DateTime.fromJSDate(ctrl.oldHeatMapEndDate).toFormat('yyyy-MM-dd');
        if (oldStartDateFormatted === ctrl.config.yAxis.categories[0]) {
          toasterService.warningWithTimeout(
            $translate.instant('toaster.heatmap-warning-title'),
            $translate.instant(
              'toaster.heatmap-warning-before-text',
              {
                value: DateTime.fromISO(oldStartDateFormatted).toFormat('dd.MM.yyyy'),
              },
              5000
            )
          );
        } else if (oldEndDateFormatted === ctrl.config.yAxis.categories.slice(-1)[0]) {
          toasterService.warningWithTimeout(
            $translate.instant('toaster.heatmap-warning-title'),
            $translate.instant('toaster.heatmap-warning-after-text', {
              value: DateTime.fromISO(oldEndDateFormatted).toFormat('dd.MM.yyyy'),
            }),
            5000
          );
        }
        if ((ctrl.oldHeatMapStartDate || ctrl.oldHeatMapEndDate) && data.yAxis?.length) {
          ctrl.updateDates(new Date(data.yAxis[0]), new Date(data.yAxis[data.yAxis.length - 1]));
          ctrl.prevSelection = angular.copy(ctrl.selection);
        }
        ctrl.totalEvents = data.count;
        $scope.$broadcast('reflow');
      }
      ctrl.oldHeatMapStartDate = null;
      ctrl.oldHeatMapEndDate = null;
      ctrl.previousLoadWasHeatmap = true;
      ctrl.chartLoading = false;
    });
  };

  const getEventListParameters = event => {
    const parameters = getParameters();
    // Pass the JSON filter query to backend if filters are set
    const environmentDataQuery = (ctrl.supportsEventHistoryFilters && ctrl.selection.envFilters?.filterJson) ? ctrl.selection.envFilters.filterJson : null;
    return {
      count: eventHistoryPageSize,
      skip: ctrl.eventHistory.length || 0,
      eventSortType: event.sortType || 'ACTIVATED',
      sortReverse: (event.reversed === undefined || event.reversed === null) ? true : event.reversed,
      cars: ctrl.selection.consist ? [] : (parameters.cars || null),
      consist: ctrl.selection.consist ? ctrl.selection.consist.value : null,
      subsystemIds: parameters.subsystemIds || null,
      eventIds: parameters.eventIds || null,
      environmentDataQuery, // JSON filter from QueryBuilder
      startDate: parameters.startDate || null,
      endDate: parameters.endDate || null,
    };
  };

  const addNewEnvDataColumns = (events) => {
    if (!ctrl.supportsEventHistoryFilters) {
      return;
    }
    
    // Create a new object to force React re-render (don't mutate existing object)
    const updatedFieldValues = { ...ctrl.envDataFieldValues };
    
    // Only collect distinct values for fields that exist in metadata
    // Do NOT add new fields dynamically - metadata endpoint is the single source of truth
    for (const event of events) {
      for (const column in event.environmentData) {
        // Only process fields that are in the metadata
        if (!ctrl.envDataFields.includes(column)) {
          continue; // Skip fields not in metadata
        }
        
        // Collect distinct values for this field
        const value = event.environmentData[column];
        if (value !== null && value !== undefined) {
          // Initialize as array if not exists (metadata pre-populates booleans)
          if (!updatedFieldValues[column]) {
            updatedFieldValues[column] = [];
          }
          
          // Convert to Set for deduplication if it's an array
          let valueSet;
          if (Array.isArray(updatedFieldValues[column])) {
            valueSet = new Set(updatedFieldValues[column]);
          } else {
            valueSet = new Set();
          }
          
          valueSet.add(String(value));
          
          // Convert back to sorted array with a comparator to avoid default alphabetical sorting
          updatedFieldValues[column] = Array.from(valueSet).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
        }
      }
    }
    
    // Replace the entire object to trigger React re-render
    ctrl.envDataFieldValues = updatedFieldValues;
    
  };

  ctrl.retryEventHistory = () => {
    if (lastEventHistoryRequest) ctrl.loadEventHistoryData({ ...lastEventHistoryRequest, retry: true });
  };

  ctrl.loadEventHistoryData = async (event = {}) => {
    if (destroyed || !ctrl.analysisConnection || !ctrl.initialized || !isEventHistoryTabActive()) return;
    if (ctrl.loadBusy) {
      if (event.reload) {
        eventHistoryGeneration += 1;
        pendingEventHistoryReload = { ...event };
      }
      return;
    }
    if (!event.reload && (!ctrl.eventHistoryHasMore || (ctrl.eventHistoryError && !event.retry))) return;
    const generation = ++eventHistoryGeneration;
    const isCurrent = () => !destroyed && generation === eventHistoryGeneration
      && ctrl.selection.chartTypeConfig.value === 'eventHistoryData' && isEventHistoryTabActive();
    lastEventHistoryRequest = { ...event };
    ctrl.loadBusy = true;
    ctrl.eventHistoryError = null;
    ctrl.showData = false;
    ctrl.chartPresentation = false;
    $scope.$applyAsync();

    try {
      // Load environment data metadata on first Event History view when the profile supports it.
      if (!ctrl.previousLoadWasEventHistory && ctrl.supportsEventHistoryFilters) {
        if (!ctrl.envDataFieldMetadata || ctrl.envDataFieldMetadata.length === 0) {
          await loadEnvDataMetadata();
        }
        const defaultsAvailable = ctrl.hasCuratedEnvColumns
          ? ctrl.defaultEnvColumns.filter(field => ctrl.envDataFields.includes(field))
          : ctrl.envDataFields;
        ctrl.selection.envData = defaultsAvailable.length > 0 ? defaultsAvailable : [...ctrl.envDataFields];
        // Default columns are display-only; sync the snapshot so $doCheck doesn't queue a duplicate reload.
        if (ctrl.prevSelection) {
          ctrl.prevSelection.envData = angular.copy(ctrl.selection.envData);
        }
      }

      if (!isCurrent()) return;
      const parameters = getEventListParameters(event);

      if (event.reload) {
        parameters.skip = 0;
        // Filters changed, so per-consist results are stale.
        ctrl.consistEvents = {};
      }

      const data = await notificationService.getHistoryEvents(parameters);
      if (!isCurrent()) return;
      if (!Array.isArray(data)) throw new Error('Invalid event history response');
      addNewEnvDataColumns(data);
      ctrl.eventHistory = event.reload ? data : ctrl.eventHistory.concat(data);
      ctrl.eventHistoryHasMore = data.length === eventHistoryPageSize;
      ctrl.eventSortType = parameters.eventSortType;
      ctrl.sortReverse = parameters.sortReverse;
      ctrl.previousLoadWasEventHistory = true;
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Unexpected error in loadEventHistoryData:', error);
      ctrl.eventHistoryError = getAlarmRuleErrorMessage(error, 'Events could not be loaded. Please try again.');
      toasterService.error('Failed to load events', ctrl.eventHistoryError);
    } finally {
      if (!destroyed) {
        ctrl.loadBusy = false;
        ctrl.chartLoading = false;
        const pending = pendingEventHistoryReload;
        pendingEventHistoryReload = null;
        if (pending) void ctrl.loadEventHistoryData(pending);
        $scope.$applyAsync();
      }
    }
  };

  ctrl.loadConsistHistoryData = async (event) => {
    if (!ctrl.analysisConnection || !ctrl.initialized) {
      return;
    }
    ctrl.loadBusy = false;
    ctrl.chartLoading = false;
    ctrl.chartPresentation = false;
  };

  // Fetch list of history events, wrapped in tryCatch, returns data if no error.
  // fetchEvents :: (Object) -> [Object]
  ctrl.fetchEvents = parameters =>
    tryCatch(async () => await notificationService.getHistoryEvents(parameters))
      .fold(e => {
        // Check if error has status code information
        const statusCode = e?.status || e?.data?.status;
        
        if (statusCode === 500 || statusCode === 400) {
          toasterService.error($translate.instant('toaster.server-error') || 
            'Server error occurred. Please check your filter values and try again.');
        }
          
        if (e?.message) {
          console.error(`Event fetch error: ${e.message}`);
        }
        // This ensures the app state remains functional for subsequent requests
        return null;
      },
        data => data);

  ctrl.exportToExcel = async () => {
    const parameters = getParameters();
    parameters.count = 50000;
    parameters.eventSortType = 'ACTIVATED';
    parameters.localTimezone = DateTime.local().zoneName;
    if (ctrl.supportsEventHistoryFilters && ctrl.selection.envFilters?.filterJson) {
      parameters.environmentDataQuery = ctrl.selection.envFilters.filterJson;
    }
    ctrl.fetching = true;
    await eventsExportService.exportToExcel(parameters, $translate.instant('export.latest-events'));
    ctrl.fetching = false;
  };

  ctrl.exportToCsv = async () => {
    const parameters = getParameters();
    parameters.count = 50000;
    parameters.eventSortType = 'ACTIVATED';
    parameters.localTimezone = DateTime.local().zoneName;
    if (ctrl.supportsEventHistoryFilters && ctrl.selection.envFilters?.filterJson) {
      parameters.environmentDataQuery = ctrl.selection.envFilters.filterJson;
    }
    ctrl.fetching = true;
    await eventsExportService.exportToCsv(parameters, $translate.instant('export.latest-events'));
    ctrl.fetching = false;
  };

  const setSelectionRight = () => {
    ctrl.selection.chartTypeConfig = angular.copy(
      ctrl.chartConfigList.find(chartConfig => chartConfig.value === ctrl.selection.chartTypeConfig.clickValue)
    );
  };

  const createFilteredEventInfo = () => {
    ctrl.eventInfoFiltered = [];
    let subsystems = angular.copy(ctrl.selection.subsystems);
    if (subsystems.length === 0 || subsystems[0].value === 'ALL') {
      ctrl.eventInfoFiltered = angular.copy(ctrl.eventInfo);
    } else {
      let ids = {};
      _.each(subsystems, sub => {
        ids[sub.id] = true;
      });
      ctrl.eventInfoFiltered = _.filter(ctrl.eventInfo, event => ids[event.subsystemId]);
      let lengthBefore = ctrl.selection.events.length;
      if (lengthBefore > 0) {
        ctrl.selection.events = _.filter(ctrl.selection.events, event => ids[event.subsystemId]);
      }
      let lengthAfter = ctrl.selection.events.length;
      if (lengthBefore > lengthAfter) {
        toasterService.warningWithTimeout(
          $translate.instant('toaster.subsystem-filter-event-removal-title'),
          $translate.instant('toaster.subsystem-filter-event-removal'),
          5000
        );
      }
    }
  };

  eventService.on('analysis-offline', () => {
    ctrl.analysisConnection = false;
    offlinePoller();
  });

  const offlinePoller = () => {
    $interval.cancel(ctrl.timeoutPromise);
    ctrl.timeoutPromise = $interval(() => {
      ctrl.analysisConnection = true;
      loadGraphData();
    }, 10000);
  };

  ctrl.setShowData = () => {
    ctrl.showData =
      ((typeof ctrl.config.series[0] !== 'undefined' && ctrl.config.series[0].data.length > 0) ||
      (typeof ctrl.config.series.data !== 'undefined' && ctrl.config.series.data.length > 0)) &&
      !ctrl.categoryLimitExceeded;
  };

  ctrl.resetAllFilters = () => {
    ctrl.selection.consist = null;
    ctrl.consistChange();
    ctrl.selection.cars = [];
    ctrl.selection.subsystems = [];
    ctrl.selection.events = [];
    ctrl.selectedPreset = null; 
    ctrl.selection.envFilters = createEmptyEnvFilters();
    if (ctrl.hasCuratedEnvColumns) {
      ctrl.selection.envData = [...ctrl.defaultEnvColumns];
    }
    ctrl.dataWindowMode = 'date';
    ctrl.dataWindowValue = null;
    ctrl.dataWindowDateRange = {
      startDate: null,
      endDate: null,
    };
    ctrl.createRuleOccurrences = null;
    ctrl.createRuleIntervalValue = null;
    ctrl.createRuleIntervalUnit = DEFAULT_ALARM_INTERVAL_UNIT;
    createFilteredEventInfo();
  };

  ctrl.refreshData = () => {
    loadGraphData();
  };

  ctrl.resetSelection = selector => {
    switch (selector) {
      case 'consist':
        ctrl.selection.consist = null;
        ctrl.consistChange();
        break;
      case 'cars':
        ctrl.selection.cars = [];
        break;
      case 'subsystems':
        ctrl.selection.subsystems = [];
        createFilteredEventInfo();
        break;
      case 'events':
        ctrl.selection.events = [];
        break;
      case 'envData':
        ctrl.selection.envData = [];
        break;
    }
  };

  ctrl.showChartType = chartType => {
    let show = ctrl.selection.chartTypeConfig.chartTypes.includes(chartType);
    if (!show && chartType === ctrl.config.chart.type) {
      ctrl.config.chart.type = angular.copy(ctrl.selection.chartTypeConfig.chartTypes[0]);
    }
    return show;
  };

  ctrl.showSelector = selector => ctrl.selection.chartTypeConfig.selectors.includes(selector);

  const getParameters = () => {
    const parameters = {
      startDate: null,
      endDate: null,
      cars: [],
      subsystemIds: [],
      eventIds: [],
      active: null,
      skip: 0,
      count: 25,
      eventSortType: ctrl.sortType,
      sortReverse: ctrl.reversed,
      export: null,
      localTimezone: DateTime.local().zoneName
    };
    
    const isTableView = ctrl.selection.chartTypeConfig && ctrl.selection.chartTypeConfig.type === 'table';
    const formatDate = (date) => {
      const dateTime = DateTime.fromJSDate(date).toUTC();
      return isTableView ? dateTime.toISO() : dateTime.toFormat('yyyy-MM-dd');
    };
  
    if (ctrl.selection.startDate && !isNaN(ctrl.selection.startDate)) {
        parameters.startDate = formatDate(ctrl.selection.startDate);
    }
    if (ctrl.selection.endDate && !isNaN(ctrl.selection.endDate)) {
        parameters.endDate = formatDate(ctrl.selection.endDate);
    }
    if (checkArray(ctrl.selection.cars)) {
      _.each(ctrl.selection.cars, function (car) {
        parameters.cars.push(car.value);
      });
    } else if (ctrl.selection.consist && checkArray(ctrl.selection.consist.cars)) {
      parameters.cars = angular.copy(ctrl.selection.consist.cars);
    }
    if (checkArray(ctrl.selection.subsystems)) {
      _.each(ctrl.selection.subsystems, function (subsystem) {
        parameters.subsystemIds.push(subsystem.value ? subsystem.value : subsystem.id);
      });
    }
    if (checkArray(ctrl.selection.events)) {
      _.each(ctrl.selection.events, function (event) {
        parameters.eventIds.push(event.value);
      });
    }
    return parameters;
  };

  const checkArray = array => typeof array !== 'undefined' && array !== null && array.length > 0;

  //TODO: Move to separate service or refactor this code for simplicity
  // Filter Preset Management - React Component Callbacks
  ctrl.loadFilterPresets = async () => {
    if (!ctrl.showPresets) { return; }
    
    ctrl.presetsLoading = true;
    ctrl.presetsError = null;
    
    try {
      const presets = await filterPresetService.getAll();
      ctrl.filterPresets = presets || [];
      $scope.$applyAsync(); // Trigger digest for React binding
    } catch (error) {
      console.error('Error loading filter presets:', error);
      ctrl.presetsError = 'Failed to load filter presets';
      $scope.$applyAsync();
    } finally {
      ctrl.presetsLoading = false;
      $scope.$applyAsync();
    }
  };

  const presetHasValues = preset => Boolean(
    preset.environmentDataQuery ||
    (preset.eventIds && Array.isArray(preset.eventIds) && preset.eventIds.length > 0) ||
    (preset.cars && Array.isArray(preset.cars) && preset.cars.length > 0) ||
    (preset.subsystemIds && Array.isArray(preset.subsystemIds) && preset.subsystemIds.length > 0) ||
    (preset.consists && Array.isArray(preset.consists) && preset.consists.length > 0) ||
    preset.consist
  );

  const getPresetConsistValue = preset => {
    if (preset.consists && Array.isArray(preset.consists) && preset.consists.length > 0) {
      return preset.consists[0];
    }
    return preset.consist;
  };

  const getPresetEnvironmentFilters = environmentDataQuery => {
    let parsedEnvironmentDataQuery = environmentDataQuery;
    if (typeof environmentDataQuery === 'string') {
      try {
        const parsed = JSON.parse(environmentDataQuery);
        if (parsed && typeof parsed === 'object') {
          parsedEnvironmentDataQuery = parsed;
        }
      } catch (error) {
        console.warn('Preset has environmentDataQuery as string, using legacy format', error);
      }
    }

    const presetQuery =
      parsedEnvironmentDataQuery && Array.isArray(parsedEnvironmentDataQuery.rules)
        ? parsedEnvironmentDataQuery
        : { combinator: 'and', rules: [] };

    return {
      query: presetQuery,
      filterJson: parsedEnvironmentDataQuery || ''
    };
  };

  const restorePresetEvents = preset => {
    if (!preset.eventIds || !Array.isArray(preset.eventIds) || preset.eventIds.length === 0) {
      ctrl.selection.events = [];
      return;
    }

    ctrl.selection.events = preset.eventIds.map((eventId) => {
      const fullEvent = ctrl.eventInfoFiltered ?
        ctrl.eventInfoFiltered.find(e => e.value === eventId) : null;

      return fullEvent || {
        value: eventId,
        title: `Event ${eventId}`
      };
    });
  };

  const restorePresetCars = preset => {
    if (!preset.cars || !Array.isArray(preset.cars) || preset.cars.length === 0) {
      ctrl.selection.cars = [];
      return;
    }

    ctrl.selection.cars = preset.cars.map((carId) => {
      const fullCar = ctrl.carList ?
        ctrl.carList.find(c => c.value === carId) : null;

      return fullCar || {
        value: carId,
        label: carId
      };
    });
  };

  const restorePresetSubsystems = preset => {
    if (!preset.subsystemIds || !Array.isArray(preset.subsystemIds) || preset.subsystemIds.length === 0) {
      ctrl.selection.subsystems = [];
      return;
    }

    ctrl.selection.subsystems = preset.subsystemIds.map((subsystemId) => {
      const fullSubsystem = ctrl.subsystemInfo ?
        ctrl.subsystemInfo.find(s => (s.value || s.id) === subsystemId) : null;

      return fullSubsystem || {
        id: subsystemId,
        value: subsystemId,
        title: `Subsystem ${subsystemId}`
      };
    });
  };

  const restorePresetConsist = preset => {
    const presetConsist = getPresetConsistValue(preset);
    if (!presetConsist) {
      ctrl.selection.consist = null;
      ctrl.consistChange();
      return;
    }

    const fullConsist = ctrl.consistList ?
      ctrl.consistList.find(c => String(c.value) === String(presetConsist)) : null;
    ctrl.selection.consist = fullConsist || { value: presetConsist, title: presetConsist, cars: [] };
    ctrl.consistChange();
  };

  ctrl.applyPreset = (preset) => {
    if (!ctrl.showPresets) { return; }
    
    // Handle deselect an item from preset filters list (preset is null)
    if (!preset) {
      ctrl.selectedPreset = null;
      ctrl.selection.envFilters = createEmptyEnvFilters();
      ctrl.eventHistory = [];
      ctrl.selection.subsystems = [];
      ctrl.selection.events = [];
      ctrl.selection.cars = [];
      ctrl.selection.consist = null;
      ctrl.consistChange();
      return;
    }
    
    // Don't re-apply if it's already the selected preset (toggle is handled in React)
    if (ctrl.selectedPreset && ctrl.selectedPreset.id === preset.id) {
      return;
    }
    
    try {
      if (presetHasValues(preset)) {
        ctrl.selection.envFilters = getPresetEnvironmentFilters(preset.environmentDataQuery);
        restorePresetEvents(preset);
        restorePresetCars(preset);
        restorePresetSubsystems(preset);
        restorePresetConsist(preset);

        ctrl.selectedPreset = preset;
        
        // Clear event history so reload starts fresh
        ctrl.eventHistory = [];
        
        toasterService.success(`Filter preset "${preset.name}" applied`);
        
        // Let $doCheck handle the reload naturally
      } else {
        toasterService.warning('Preset has no filter configuration');
      }
    } catch (error) {
      console.error('Error applying preset:', error);
      toasterService.error('Failed to apply filter preset');
    }
  };

  ctrl.savePreset = async (preset) => {
    if (!ctrl.showPresets) {
      toasterService.error('forbidden-error.title', 'forbidden-error.message');
      return false;
    }
    try {
      const presetId = await filterPresetService.create(preset);
      if (!presetId) {
        throw new Error('Backend rejected filter preset create request');
      }
      toasterService.success(`Filter preset "${preset.name}" saved successfully`);
      
      // Reload presets to include the new one
      await ctrl.loadFilterPresets();
      
      return true;
    } catch (error) {
      console.error('Error saving preset:', error);
      toasterService.error('Failed to save filter preset');
      throw error;
    }
  };

  ctrl.deletePreset = async (preset) => {
    if (!ctrl.showPresets) {
      toasterService.error('forbidden-error.title', 'forbidden-error.message');
      return false;
    }
    try {
      await filterPresetService.delete(preset.id);
      toasterService.success(`Filter preset "${preset.name}" deleted`);
      
      // Clear selection if the deleted preset was selected
      if (ctrl.selectedPreset && ctrl.selectedPreset.id === preset.id) {
        ctrl.selectedPreset = null;
        $scope.$applyAsync();
      }
      
      // Reload presets
      await ctrl.loadFilterPresets();
    } catch (error) {
      console.error('Error deleting preset:', error);
      toasterService.error('Failed to delete filter preset');
    }
  };

  // Helper functions to get current filter values for saving presets
  const syncCurrentEventsFromSelection = () => {
    if (Array.isArray(ctrl.selection?.events) && ctrl.selection.events.length > 0) {
      ctrl.currentEvents = ctrl.selection.events;
    } else {
      ctrl.currentEvents = [];
    }
  };

  const syncCurrentCarsFromSelection = () => {
    if (Array.isArray(ctrl.selection?.cars) && ctrl.selection.cars.length > 0) {
      ctrl.currentCars = ctrl.selection.cars.map(car => car.value);
    } else {
      ctrl.currentCars = [];
    }
  };

  const syncCurrentSubsystemsFromSelection = () => {
    if (Array.isArray(ctrl.selection?.subsystems) && ctrl.selection.subsystems.length > 0) {
      ctrl.currentSubsystems = ctrl.selection.subsystems;
    } else {
      ctrl.currentSubsystems = [];
    }
  };

  // Initialize current filter values
  ctrl.currentCars = [];
  ctrl.currentEvents = [];
  ctrl.currentCarObjects = [];
  ctrl.currentSubsystems = [];

  // Watch for changes in selections to update current filter values
  $scope.$watch(() => ctrl.selection.events, () => {
    syncCurrentEventsFromSelection();
  }, true);

  $scope.$watch(() => ctrl.selection.cars, () => {
    syncCurrentCarsFromSelection();
  }, true);

  $scope.$watch(() => ctrl.selection.subsystems, () => {
    syncCurrentSubsystemsFromSelection();
  }, true);
  
  // Re-filter the Consist-scope table's parent rows whenever the Consist dropdown changes.
  $scope.$watch(() => ctrl.selection.consist, () => {
    updateDataScopeConsistListFiltered();
  });
  // TODO: Check if needed
  // ctrl.$on('gridlet-init', function() {
  //   ctrl.$broadcast('reflow');
  // });

  // ctrl.$on('gridlet-refresh', function() {
  //   ctrl.$broadcast('reflow');
  // });

  ctrl.selectChartType = chartType => {
    ctrl.config.chart.type = chartType;
  };

  ctrl.nextTenDays = () => {
    ctrl.oldHeatMapEndDate = angular.copy(ctrl.selection.endDate);
    ctrl.selection.startDate = new Date(angular.copy(ctrl.config.yAxis.categories.slice(-1)[0]));
    ctrl.selection.endDate = null;
    loadGraphData();
  };

  ctrl.previousTenDays = () => {
    ctrl.oldHeatMapStartDate = angular.copy(ctrl.selection.startDate);
    ctrl.selection.endDate = new Date(angular.copy(ctrl.config.yAxis.categories[0]));
    ctrl.selection.startDate = null;
    loadGraphData();
  };

  ctrl.onEnvQueryValidityChange = (isValid) => {
    ctrl.envQueryValid = isValid;
  };

  ctrl.onReportFilterSelectionChange = selection => {
    ctrl.selection = {
      ...ctrl.selection,
      ...selection,
    };
    ctrl.consistChange();
    createFilteredEventInfo();
  };

  const getAlarmRuleCriteria = () => ({
    currentFilters: {
      consists: ctrl.selection.consist ? [ctrl.selection.consist.value] : [],
      cars: ctrl.currentCars,
      subsystemIds: ctrl.currentSubsystems.map(subsystem => subsystem.id),
      eventNumbers: ctrl.currentEvents.map(event => event.value),
      environmentDataQuery: ctrl.envQueryValid ? ctrl.selection.envFilters.filterJson : null,
      dataWindowMode: ctrl.dataWindowMode,
      dataWindowValue: ctrl.dataWindowValue,
      dataWindowSeconds: selectionToDataWindowSeconds({
        ...ctrl.selection,
        dataWindowMode: ctrl.dataWindowMode,
        dataWindowValue: ctrl.dataWindowValue,
      }, ctrl.dataWindowDefaultHours),
    },
    criteriaSummary: {
      consists: ctrl.selection.consist ? [ctrl.selection.consist.title] : [],
      cars: ctrl.currentCars,
      subsystems: ctrl.currentSubsystems.map(subsystem => `${subsystem.name} (${subsystem.id})`),
      events: ctrl.currentEvents.map(event => `${event.title} [${event.value}]`),
      environmentQuery: ctrl.envQueryValid ? formatEnvQueryGroup(ctrl.selection.envFilters.filterJson) : '',
    },
  });

  const getAlarmReportPayload = currentFilters => {
    const parameters = getParameters();

    return {
      occurrence: Number(ctrl.createRuleOccurrences),
      intervalSeconds: alarmIntervalToSeconds(ctrl.createRuleIntervalValue, ctrl.createRuleIntervalUnit),
      dataWindowSeconds: selectionToDataWindowSeconds({
        ...ctrl.selection,
        dataWindowMode: ctrl.dataWindowMode,
        dataWindowValue: ctrl.dataWindowValue,
      }, ctrl.dataWindowDefaultHours),
      eventFilter: {
        count: 0,
        skip: 0,
        eventSortType: parameters.eventSortType,
        startDate: parameters.startDate || null,
        endDate: parameters.endDate || null,
        cars: parameters.cars,
        sortReverse: parameters.sortReverse,
        searchString: null,
        subsystemIds: parameters.subsystemIds,
        eventIds: parameters.eventIds,
        consist: currentFilters.consists[0] || null,
        localTimezone: parameters.localTimezone,
        environmentDataQuery: currentFilters.environmentDataQuery,
      },
    };
  };

  // Opens the Create Alarm Rule form, seeded with the History Events tab's current filter
  // selections (same values already fed into Filter Presets - see filter-presets bindings above).
  ctrl.openCreateAlarmRuleModal = () => {
    if (!userService.hasAccessAlarms()) {
      toasterService.error('forbidden-error.title', 'forbidden-error.message');
      return;
    }
    const { currentFilters, criteriaSummary } = getAlarmRuleCriteria();

    $uibModal
      .open({
        component: 'alarmRuleCreateDialog',
        windowClass: 'alarm-rule-view-modal-window',
        backdrop: 'static',
        keyboard: false,
        resolve: {
          currentFilters: () => currentFilters,
          criteriaSummary: () => criteriaSummary,
          initialFormValues: () => ({
            occurrences: ctrl.createRuleOccurrences,
            alarmInterval: {
              value: ctrl.createRuleIntervalValue,
              unit: ctrl.createRuleIntervalUnit,
            },
          }),
        },
      })
      .result.then(() => {
        toasterService.success('Alarm rule created');
      }, angular.noop);
  };

  ctrl.openRunAlarmReportModal = () => {
    if (!userService.hasAccessAlarms()) {
      toasterService.error('forbidden-error.title', 'forbidden-error.message');
      return;
    }
    const { currentFilters, criteriaSummary } = getAlarmRuleCriteria();

    $uibModal.open({
      component: 'alarmEventReportDialog',
      windowClass: 'alarm-rule-view-modal-window',
      backdrop: 'static',
      keyboard: false,
      resolve: {
        reportPayload: () => getAlarmReportPayload(currentFilters),
        criteriaSummary: () => ({
          ...criteriaSummary,
          intervalDisplay: {
            value: ctrl.createRuleIntervalValue,
            unit: ctrl.createRuleIntervalUnit,
          },
          dataWindowDisplay: {
            mode: currentFilters.dataWindowMode || 'date',
            value: currentFilters.dataWindowValue,
          },
        }),
        createRuleFromReport: () => ctrl.openCreateAlarmRuleModal,
      },
    });
  };

  // Environment filters management
  ctrl.onEnvFiltersChanged = (filterData) => {
    if (!ctrl.supportsAdvancedAnalysisFilters) { return; }
    
    // If we have a selected preset and QueryBuilder is trying to reset to empty,
    // ignore it - the preset filter takes priority
    if (ctrl.selectedPreset && (!filterData.filterJson || filterData.filterJson === '')) {
      return;
    }
    
    // If user manually changes filter in QueryBuilder, clear the selected preset filter
    if (ctrl.selectedPreset && filterData.filterJson && filterData.filterJson !== ctrl.selection.envFilters.filterJson) {
      ctrl.selectedPreset = null;
    }
    
    // filterData contains {query, filterJson} - only environment data filters
    ctrl.selection.envFilters = filterData;
    // Don't manually call loadGraphData - $doCheck will detect the change and trigger it automatically
  };

  ctrl.loadGraphData = loadGraphData;
}

angular.module('analysis.module').controller('AnalysisController', AnalysisController);